/**
 * avatar.js — is this avatar a real photo, or the seeded stock placeholder?
 *
 * 2026-06-21 REWRITE. The 2026-07-16 version matched the placeholder by
 * FILENAME PREFIX (`/avatar_default-user`, `/avatar_default_`). The avatar
 * upload route names files `avatar_{user_id}_{hash}` — and one real user's id
 * is literally `default-user`, so EVERY photo that user uploaded was named
 * `avatar_default-user_*` and permanently classified as the stock silhouette.
 * Re-uploading could never fix it: it reproduced the same pattern. The same
 * prefix rule in `backend/utils/upload_access.py` was ALSO serving that user's
 * real photo to unauthenticated requests. One mistake, two faults.
 *
 * The rule is now an EXACT FILENAME match against the known seeded placeholder
 * files — a set no upload can produce, for any user id, because the upload path
 * always emits `avatar_<user_id>_<8 hex><ext>`.
 *
 * This list MUST stay identical to `backend/utils/avatar_placeholder.py`;
 * `backend/tests/test_avatar_placeholder_rule.py` fails if they diverge. That
 * divergence is exactly what produced the two faults from one mistake.
 */

export const SEED_PLACEHOLDER_FILENAMES = [
  // The stock silhouette seeded before 2026-07 (found on the row that reported
  // "my photo stopped showing" — see the 2026-07-16 RCA).
  'avatar_default-user_51cacbf2.png',
  // A byte-identical second copy of that same silhouette (md5 9cd53a2f…,
  // 100x100, 287 bytes). Found by hashing every avatar_default-user_* file:
  // two seeds, eight real photos. Miss it and a row pointing at it renders a
  // stranger's silhouette as if it were a photo.
  'avatar_default-user_0d9c015a.png',
  // Canonical target of migration 0070: a name the upload path can never
  // produce (no `_<8 hex>` suffix), so this class of collision cannot recur.
  'avatar_seed_placeholder.png',
];

/** Filename (lower-cased, no query string) of a URL or path. */
function filenameOf(raw) {
  const noQuery = String(raw).split('?')[0].split('#')[0];
  return noQuery.substring(noQuery.lastIndexOf('/') + 1).toLowerCase();
}

export function isSeedPlaceholder(raw) {
  if (!raw || typeof raw !== 'string') return false;
  return SEED_PLACEHOLDER_FILENAMES.includes(filenameOf(raw));
}

export function isRealAvatar(raw) {
  if (!raw || typeof raw !== 'string') return false;
  if (!raw.trim()) return false;
  return !isSeedPlaceholder(raw);
}

/**
 * normalizeAvatarUrl — the URL to render, or `null` when there is no real
 * photo. Also keeps the pre-existing Dropbox `dl=0` → `raw=1` rewrite.
 */
export function normalizeAvatarUrl(raw) {
  if (!isRealAvatar(raw)) return null;
  if (raw.includes('dropbox.com') && raw.includes('dl=0')) {
    return raw.replace('dl=0', 'raw=1');
  }
  return raw;
}
